import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
} from "../../components/Component";
import { handleDeleteApis, handleGetApis } from "../../service/Api";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

// Published = live (or scheduled when its date is in the future); Draft = only here.
const statusOf = (post) => {
  if (!post.is_published) return { label: "Draft", color: "light" };
  if (post.published_at && new Date(post.published_at) > new Date()) return { label: "Scheduled", color: "info" };
  return { label: "Published", color: "success" };
};

const JournalList = () => {
  const [state, setState] = useState({ loading: true, posts: [], error: "" });
  const [version, setVersion] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    handleGetApis("admin/journal").then((res) => {
      if (cancelled) return;
      setState({
        loading: false,
        posts: res?.data?.success ? res.data.data : [],
        error: res?.data?.success ? "" : "Couldn't load articles.",
      });
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    const res = await handleDeleteApis(`admin/journal/${target.id}`);
    if (res?.data?.success) {
      toast.success("Article deleted");
      setVersion((v) => v + 1);
    } else {
      toast.error(res?.data?.message || res?.message || "Couldn't delete.");
    }
  };

  return (
    <React.Fragment>
      <Head title="Journal" />
      <ToastContainer position="top-right" autoClose={3000} />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Journal</BlockTitle>
              <BlockDes className="text-soft">Articles shown at /journal. Each article gets its own page on Google.</BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Link to="/journal/new" className="btn btn-primary">
                <Icon name="plus" /> <span>New article</span>
              </Link>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered">
            <div className="card-inner p-0">
              {state.loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : state.error ? (
                <div className="text-center py-5 text-danger">{state.error}</div>
              ) : state.posts.length === 0 ? (
                <div className="text-center py-5">
                  <h6 className="title">No articles yet</h6>
                  <p className="text-soft fs-13px">The Journal page stays hidden from Google until the first article is published.</p>
                </div>
              ) : (
                <DataTableBody>
                  <DataTableHead>
                    <DataTableRow>
                      <span>Article</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Category</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>Status</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span>Publish date</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span>Actions</span>
                    </DataTableRow>
                  </DataTableHead>
                  {state.posts.map((post) => {
                    const status = statusOf(post);
                    return (
                      <DataTableItem key={post.id}>
                        <DataTableRow>
                          <span className="tb-lead">{post.title}</span>
                          <span className="sub-text">/journal/{post.slug}</span>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="tb-sub">{post.category || "—"}</span>
                        </DataTableRow>
                        <DataTableRow>
                          <Badge color={status.color} className="badge-dim">
                            {status.label}
                          </Badge>
                        </DataTableRow>
                        <DataTableRow size="md">
                          <span className="tb-sub">{formatDate(post.published_at)}</span>
                        </DataTableRow>
                        <DataTableRow className="nk-tb-col-tools text-end">
                          <ul className="nk-tb-actions gx-1">
                            <li>
                              <Link to={`/journal/${post.id}`} className="btn btn-sm btn-light btn-icon" aria-label={`Edit ${post.title}`}>
                                <Icon name="edit" />
                              </Link>
                            </li>
                            <li>
                              <Button size="sm" color="light" className="btn-icon" onClick={() => setDeleteTarget(post)} aria-label={`Delete ${post.title}`}>
                                <Icon name="trash" />
                              </Button>
                            </li>
                          </ul>
                        </DataTableRow>
                      </DataTableItem>
                    );
                  })}
                </DataTableBody>
              )}
            </div>
          </div>
        </Block>

        <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
          <ModalHeader toggle={() => setDeleteTarget(null)}>Delete article?</ModalHeader>
          <ModalBody>
            “{deleteTarget?.title}” will be removed from the site. Its page will stop working — this cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default JournalList;
